import { Box } from '@mui/material';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * SEGMENTY — kanoniczny przełącznik wyboru jednej opcji z kilku
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * POWÓD POWSTANIA. Filtr okna miał trzy niezależne przyciski w złotej obwódce, bez wspólnego toru
 * i BEZ STANU WYBRANEGO. Audyt parytetu zapisał to jednym zdaniem: „na zrzucie nie widać, które
 * okno jest aktywne". To nie jest usterka kosmetyczna — użytkownik czyta liczby, nie wiedząc,
 * za jaki okres są policzone.
 *
 * DLACZEGO NIE `ToggleButtonGroup` Z MUI. Makieta ma tor (wspólne tło pigułki) i pojedynczy
 * element wybrany na złoto. MUI rysuje grupę obwiedzionych przycisków — inna waga optyczna,
 * inne proporcje, inna hierarchia. Przy dwóch komponentach obok siebie natychmiast widać,
 * że pochodzą z dwóch światów.
 *
 * DOSTĘPNOŚĆ. `role="radiogroup"` z `role="radio"` i `aria-checked`, nie lista przycisków:
 * to jest wybór JEDNEJ opcji z zamkniętego zbioru i czytnik ekranu ma to usłyszeć. Strzałki
 * przesuwają wybór, bo tak działa grupa przycisków radiowych i użytkownik klawiatury tego
 * oczekuje — bez tego trzeba tabulatorem przechodzić przez każdą opcję z osobna.
 */

export interface Segment<T extends string> {
  wartosc: T;
  etykieta: string;
}

export function Segmenty<T extends string>({
  opcje,
  wybrana,
  onZmiana,
  etykieta,
}: {
  opcje: readonly Segment<T>[];
  wybrana: T | null;
  onZmiana: (w: T) => void;
  /** Nazwa całej grupy — bez niej czytnik ekranu przeczyta same etykiety, bez kontekstu. */
  etykieta: string;
}) {
  const naKlawisz = (e: React.KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const krok = e.key === 'ArrowRight' ? 1 : -1;
    const nastepna = opcje[(i + krok + opcje.length) % opcje.length];
    if (nastepna) onZmiana(nastepna.wartosc);
  };

  return (
    <Box
      role="radiogroup"
      aria-label={etykieta}
      sx={{
        display: 'inline-flex',
        p: '3px',
        borderRadius: 'var(--r-pigulka)',
        background: 'var(--tlo-glebokie)',
        gap: '2px',
      }}
    >
      {opcje.map((o, i) => {
        const aktywna = o.wartosc === wybrana;
        return (
          <Box
            key={o.wartosc}
            component="button"
            type="button"
            role="radio"
            aria-checked={aktywna}
            tabIndex={aktywna || (wybrana === null && i === 0) ? 0 : -1}
            onClick={() => onZmiana(o.wartosc)}
            onKeyDown={(e: React.KeyboardEvent) => naKlawisz(e, i)}
            sx={{
              px: '15px',
              py: '6px',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 'var(--r-pigulka)',
              fontSize: 'var(--st-drobny)',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: aktywna ? 'var(--atrament)' : 'var(--tekst-slaby)',
              background: aktywna ? 'var(--zloto)' : 'transparent',
              boxShadow: aktywna ? 'var(--cien-1)' : 'none',
              transition:
                'background var(--czas-szybko) var(--krzywa-wejscie), color var(--czas-szybko), box-shadow var(--czas-szybko)',
              '&:hover': aktywna ? undefined : { color: 'var(--tekst)' },
            }}
          >
            {o.etykieta}
          </Box>
        );
      })}
    </Box>
  );
}
